import React, { createContext, useState, useEffect } from "react";

export const DataContext = createContext();

const API_BASE_URL =
    window.location.hostname === "localhost" || window.location.hostname.startsWith("10.")
        ? "https://localhost:7022/api"
        : "https://dysband.com/api";

const FACEBOOK_API_VERSION = "v22.0";
const PAGE_ID = process.env.REACT_APP_FB_PAGE_ID;
const ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN;

const FIELDS = [
    "cover",
    "description",
    "end_time",
    "id",
    "is_canceled",
    "name",
    "place",
    "scheduled_publish_time",
    "start_time",
    "ticket_uri",
    "timezone",
    "type",
    "category"
].join(",");

export const DataProvider = ({ children }) => {
    const [events, setEvents] = useState([]);
    const [showPastEvents, setShowPastEvents] = useState(false);

    async function fetchEventsFromDb() {
        try {
            const res = await fetch(`${API_BASE_URL}/FacebookEvents`);
            const text = await res.text(); // always read text for better debugging

            if (!res.ok) {
                console.error("Failed fetching events:", res.status, text);
                throw new Error(`Status ${res.status}: ${text}`);
            }

            const data = JSON.parse(text); // parse JSON after reading text
            setEvents(data);
            console.log(`Fetched ${data.length} events from DB`);
            return data;
        } catch (err) {
            console.error("Error fetching events from DB:", err);
            setEvents([]); // reset on error
            return [];
        }
    }


    // Helper to fetch all pages from FB Graph
    async function fetchAllPages(url) {
        let events = [];
        let nextUrl = url;
        while (nextUrl) {
            const res = await fetch(nextUrl);
            const data = await res.json();
            if (data.data) events = [...events, ...data.data];
            nextUrl = data.paging?.next || null;
        }
        return events;
    }

    const updateFaceBookEventsDB = async () => {
        try {
            // Go back 1 year for safety
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            const since = oneYearAgo.toISOString().split(".")[0];

            const PAST_EVENTS_URL = `https://graph.facebook.com/${FACEBOOK_API_VERSION}/${PAGE_ID}/events?fields=${FIELDS}&time_filter=past&since=${since}&access_token=${ACCESS_TOKEN}`;
            const UPCOMING_EVENTS_URL = `https://graph.facebook.com/${FACEBOOK_API_VERSION}/${PAGE_ID}/events?fields=${FIELDS}&time_filter=upcoming&since=${since}&access_token=${ACCESS_TOKEN}&_=${Date.now()}`;

            // Fetch both past + upcoming
            const [pastEvents, upcomingEvents] = await Promise.all([
                fetchAllPages(PAST_EVENTS_URL),
                fetchAllPages(UPCOMING_EVENTS_URL)
            ]);

            // Merge and filter out invalid events
            const combinedEvents = [...pastEvents, ...upcomingEvents]
                .filter(e => e.id) // must have an ID
                .map(e => {
                    const startTime = e.start_time ? new Date(e.start_time).toISOString() : new Date().toISOString();
                    const endTime = e.end_time ? new Date(e.end_time).toISOString() : startTime;
                    const scheduledPublish = e.scheduled_publish_time ? new Date(e.scheduled_publish_time).toISOString() : startTime;

                    return {
                        id: e.id ?? "",
                        name: e.name ?? "",
                        description: e.description ?? "",
                        start_time: startTime,        // <- must match [JsonPropertyName("start_time")]
                        end_time: endTime,
                        type: e.type ?? "Event",      // <- must match [JsonPropertyName("type")]
                        timezone: e.timezone ?? "",
                        ticket_uri: e.ticket_uri ?? "",
                        category: e.category ?? "",
                        is_canceled: e.is_canceled ?? false,
                        scheduled_publish_time: scheduledPublish, // <- must match [JsonPropertyName("scheduled_publish_time")]
                        cover: {
                            id: e.cover?.id ?? e.id ?? "placeholder",
                            source: e.cover?.source ?? ""
                        },
                        place: {
                            name: e.place?.name ?? ""
                        }
                    };
                })

                .sort((a, b) => new Date(a.StartTimeRaw) - new Date(b.StartTimeRaw));

            console.log("Sending to bulk-upsert:", combinedEvents);

            // Bulk upsert into DB
            const res = await fetch(`${API_BASE_URL}/FacebookEvents/bulk-upsert`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(combinedEvents)
            });

            const text = await res.text();
            if (!res.ok) {
                console.error("Bulk-upsert failed:", res.status, text);
                throw new Error(`Bulk-upsert failed: ${res.status}`);
            }

            console.log("Database updated with today and future events:", text);

            // Refresh events from DB
            await fetchEventsFromDb();
        } catch (err) {
            console.error("Error updating events from Facebook:", err);
        }
    };






    // On mount, update DB from FB
    //useEffect(() => {
    //    updateFaceBookEventsDB();
    //}, []);

    // Re-fetch DB when toggling past/future
    useEffect(() => {
        fetchEventsFromDb();
    }, [showPastEvents]);

    return (
        <DataContext.Provider
            value={{
                events,
                fetchEvents: fetchEventsFromDb,
                updateFaceBookEventsDB,
                showPastEvents,
                setShowPastEvents
            }}
        >
            {children}
        </DataContext.Provider>
    );
};
